import { useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG, buildApiUrl, handleApiError } from '../constants/api';

export const useNFTAPI = (account) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Track in-flight requests to prevent duplicate calls
  const inFlightRef = useRef(new Map());

  // Fetch all NFTs with optional filters (with retry logic and deduplication)
  const fetchNFTs = useCallback(async (filters = {}, retries = 3) => {
    // Create a unique key for this request to prevent duplicates
    const requestKey = JSON.stringify(filters);
    
    // If same request is already in flight, return the existing promise
    if (inFlightRef.current.has(requestKey)) {
      return inFlightRef.current.get(requestKey);
    }
    
    setLoading(true);
    setError(null);
    
    let lastError;
    
    // Create the request promise
    const requestPromise = (async () => {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          // If limit/offset are not explicitly set, fetch ALL NFTs using pagination
          const shouldPaginate = !filters.limit && !filters.offset;
          let allNFTs = [];
          let offset = 0;
          const limit = API_CONFIG.DEFAULT_LIMIT;
          let hasMore = true;
          let pageCount = 0;
          const maxPages = 1000; // Safety limit to prevent infinite loops

          // Fetch all pages if pagination is needed
          while (hasMore && pageCount < maxPages) {
            const params = {
              ...filters,
              limit: limit,
              offset: offset
            };

            // Create abort controller for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000); // Increased timeout for pagination
            
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.NFTS, params), {
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Transform API data to match frontend format
            const transformedNFTs = data.map(nft => ({
              tokenId: nft.token_id.toString(),
              id: nft.token_id.toString(),
              owner: nft.owner,
              seller: nft.seller,
              price: nft.price === '0' ? '0' : (parseFloat(nft.price) / 1e18).toString(), // Convert wei to ether
              tokenURI: nft.token_uri,
              listedAt: nft.listed_at?.toString() || '0',
              expires_at: nft.expires_at?.toString() || '0',
              tier: nft.tier,
              forSale: Boolean(nft.for_sale),
              name: nft.name,
              description: nft.description,
              image: nft.image_url,
              attributes: [] // API doesn't provide attributes, but we keep the field for compatibility
            }));


            allNFTs = [...allNFTs, ...transformedNFTs];
            pageCount++;
            
            // If we got fewer items than the limit, we've reached the end
            if (data.length < limit || !shouldPaginate) {
              hasMore = false;
            } else {
              offset += limit;
            }
          }

          setNfts(allNFTs);
          
          // Remove from in-flight map on success
          inFlightRef.current.delete(requestKey);
          setLoading(false);

          return allNFTs;
        } catch (err) {
          lastError = err;
          // Only retry on network errors, not on 4xx errors
          if (attempt < retries - 1 && (!err.response || err.response.status >= 500)) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Exponential backoff
            continue;
          }
          const errorMessage = handleApiError(err);
          setError(errorMessage.message);
          // Remove from in-flight map on error
          inFlightRef.current.delete(requestKey);
          setLoading(false);
          throw errorMessage;
        }
      }
      
      // If we get here, all retries failed
      inFlightRef.current.delete(requestKey);
      setLoading(false);
      const errorMessage = handleApiError(lastError);
      setError(errorMessage.message);
      throw errorMessage;
    })();
    
    // Store the promise in the in-flight map
    inFlightRef.current.set(requestKey, requestPromise);
    return requestPromise;
  }, []);

  // Fetch NFTs owned by a specific address
  const fetchNFTsByOwner = useCallback(async (ownerAddress, filters = {}) => {
    return fetchNFTs({
      owner: ownerAddress,
      ...filters
    });
  }, [fetchNFTs]);

  // Fetch NFTs listed for sale
  const fetchNFTsForSale = useCallback(async (filters = {}) => {
    return fetchNFTs({
      for_sale: true,
      ...filters
    });
  }, [fetchNFTs]);

  // Fetch a single NFT by ID (with retry logic)
  const fetchNFTById = useCallback(async (tokenId, retries = 3) => {
    setLoading(true);
    setError(null);
    
    let lastError;
    
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // Create abort controller for timeout (AbortSignal.timeout not available in all browsers)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NFT_BY_ID}/${tokenId}`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('NFT not found');
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const nft = await response.json();
        
        // Transform API data to match frontend format
        const transformedNFT = {
          tokenId: nft.token_id.toString(),
          id: nft.token_id.toString(),
          owner: nft.owner,
          seller: nft.seller,
          price: nft.price === '0' ? '0' : (parseFloat(nft.price) / 1e18).toString(), // Convert wei to ether
          tokenURI: nft.token_uri,
          listedAt: nft.listed_at?.toString() || '0',
          tier: nft.tier,
          forSale: Boolean(nft.for_sale),
          name: nft.name,
          description: nft.description,
          image: nft.image_url,
          attributes: [] // API doesn't provide attributes, but we keep the field for compatibility
        };

        setLoading(false);
        return transformedNFT;
      } catch (err) {
        lastError = err;
        // Only retry on network errors, not on 4xx errors
        if (attempt < retries - 1 && (!err.response || err.response.status >= 500)) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Exponential backoff
          continue;
        }
        setLoading(false);
        const errorMessage = handleApiError(err);
        setError(errorMessage.message);
        throw errorMessage;
      }
    }
    
    // If we get here, all retries failed
    setLoading(false);
    const errorMessage = handleApiError(lastError);
    setError(errorMessage.message);
    throw errorMessage;
  }, []);

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SYNC_STATUS}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setSyncStatus(data);
      return data;
    } catch (err) {
      console.warn('Failed to fetch sync status:', err);
      return null;
    }
  }, []);

  // Check API health
  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.HEALTH}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.status === 'healthy';
    } catch (err) {
      console.warn('API health check failed:', err);
      return false;
    }
  }, []);

  // Trigger manual sync
  const triggerSync = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SYNC}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = handleApiError(err);
      throw errorMessage;
    }
  }, []);

  // Trigger full sync
  const triggerFullSync = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FULL_SYNC}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = handleApiError(err);
      throw errorMessage;
    }
  }, []);

  // Auto-refresh data when account changes
  // Note: This effect is intentionally minimal - actual fetching is handled by useContract
  // to avoid duplicate API calls and infinite loops
  useEffect(() => {
    // Only set initial state, actual fetching happens in useContract
    if (!account) {
      setNfts([]);
    }
  }, [account]);

  // Periodic sync status check
  useEffect(() => {
    fetchSyncStatus();
    
    const interval = setInterval(fetchSyncStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  return {
    nfts,
    loading,
    error,
    syncStatus,
    fetchNFTs,
    fetchNFTsByOwner,
    fetchNFTsForSale,
    fetchNFTById,
    fetchSyncStatus,
    checkHealth,
    triggerSync,
    triggerFullSync
  };
};
